import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Button } from '../atoms';
import { PageHeader } from '../shell';
import { useToast } from '../organisms';
import { relativeTime } from '../lib/notifications';
import { formatDateTime } from '../lib/orgTimezone';

/**
 * Email approvals — every drafted client email that is waiting for a manager's
 * decision. A savings result, a board-order notice or a client template is written
 * to the outbox unapproved and sends only once someone here (or in the modal that
 * drafted it) approves it. Before this page, closing that modal hid the draft; on ATA
 * one waited two weeks. Each row names the kind, who it goes to, the record it is
 * about (a link) and how long it has waited; Approve sends it, Discard never does.
 *
 *   GET  /api/email-outbox/pending-approval
 *   POST /api/email-outbox/:id/approve   approve → the keystone flush sends it
 *   POST /api/email-outbox/:id/reject    status='skipped', never sends
 *
 * The money-blind role receives rows without `subject` (a rendered subject can quote
 * values and tax dollars), so the subject line is optional here.
 */
export interface PendingApproval {
  outbox_id: string;
  draft_kind: string | null;
  kind_label: string;
  to: string[];
  cc: string[];
  status: string;
  approved: boolean;
  related_entity_type: string | null;
  related_entity_id: string | null;
  related_label: string | null;
  related_url: string | null;
  client_name: string | null;
  created_at: string;
  subject?: string;
}

export function EmailApprovals() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const highlighted = params.get('draft');

  const query = useQuery<{ rows: PendingApproval[] }>({
    queryKey: ['email-approvals'],
    queryFn: ({ signal }) => api.get<{ rows: PendingApproval[] }>('/api/email-outbox/pending-approval', signal),
  });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['email-approvals'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };
  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/api/email-outbox/${id}/approve`, {}),
    onSuccess: () => { toast({ variant: 'success', message: 'Email approved and sent.' }); invalidate(); },
    onError: (err) => toast({ variant: 'error', message: (err as Error).message.replace(/^HTTP \d+:\s*/, '') || 'Could not approve the email' }),
  });
  const discard = useMutation({
    mutationFn: (id: string) => api.post(`/api/email-outbox/${id}/reject`, {}),
    onSuccess: () => { toast({ variant: 'info', message: 'Draft discarded. Nothing was sent.' }); invalidate(); },
    onError: (err) => toast({ variant: 'error', message: (err as Error).message.replace(/^HTTP \d+:\s*/, '') || 'Could not discard the draft' }),
  });

  const rows = query.data?.rows ?? [];
  const busy = approve.isPending || discard.isPending;

  return (
    <>
      <PageHeader title="Email approvals" subtitle={query.isLoading ? 'Loading…' : `${rows.length} waiting for a decision`} />
      {query.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading drafts…</p>
      ) : query.error ? (
        <p style={{ color: 'var(--color-status-red)' }}>Couldn’t load the approval queue.</p>
      ) : rows.length === 0 ? (
        <div style={{ padding: 'var(--sp-6)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center' }}>
          Nothing is waiting. A savings result, board order or client template drafted for approval will appear here until someone approves or discards it.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
          {rows.map((d) => (
            <div key={d.outbox_id} data-testid="approval-row" style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start', padding: 'var(--sp-3)', background: 'var(--color-card)', border: `1px solid ${d.outbox_id === highlighted ? 'var(--brand-primary)' : 'var(--color-border)'}`, borderRadius: 'var(--r-lg)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge variant="neutral">{d.kind_label}</Badge>
                  <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{d.subject ?? `${d.kind_label} email`}</span>
                </div>
                <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
                  To {d.to.join(', ')}{d.cc.length ? ` · cc ${d.cc.join(', ')}` : ''}
                </p>
                <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                  {d.related_url && d.related_label ? (
                    <button type="button" onClick={() => navigate(d.related_url!)} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--brand-primary)' }}>
                      {d.related_label}
                    </button>
                  ) : (d.related_label ?? 'No linked record')}
                  {' · '}
                  <span title={formatDateTime(d.created_at)}>Drafted {relativeTime(d.created_at)}</span>
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--sp-1)', flexShrink: 0 }}>
                <Button variant="primary" size="sm" onClick={() => approve.mutate(d.outbox_id)} disabled={busy}>Approve and send</Button>
                <Button variant="ghost" size="sm" onClick={() => discard.mutate(d.outbox_id)} disabled={busy}>Discard</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
